/**
 * @file AdditionalUserFormModal.js
 * @description Modal wrapper for the AdditionalUserForm component.
 */

import { useEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './modals.module.scss';
import AdditionalUserForm from '../../additional-users/AdditionalUserForm';

/**
 * AdditionalUserFormModal component that wraps AdditionalUserForm in a modal.
 * @param {Object} props - Component props.
 * @param {string} [props.userId] - The user ID for editing, undefined for creating.
 * @param {Function} props.onHide - Callback to close the modal.
 * @param {Function} [props.onSuccess] - Callback when form action succeeds.
 * @param {number} [props.zIndex] - The z-index for the modal.
 * @returns {React.ReactElement} The rendered modal component.
 */
function AdditionalUserFormModal({ userId, onHide, onSuccess, zIndex = 1040 }) {
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, []);

    return (
        <div
            className={`${styles.container} ${styles.additionalUserFormModal} modal-backdrop`}
            style={{ zIndex }}
        >
            <div
                className="modal fade show display-block"
                tabIndex="-1"
                aria-modal="true"
                role="dialog"
            >
                <div
                    className={`modal-dialog modal-xl modal-dialog-centered ${styles.modalSizing}`}
                >
                    <div
                        className={`modal-content ${styles.modalContent} ${styles.additionalUserModalContent}`}
                    >
                        <AdditionalUserForm
                            userId={userId}
                            onClose={onHide}
                            onSuccess={onSuccess}
                            isModal={true}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

AdditionalUserFormModal.propTypes = {
    userId: PropTypes.string,
    onHide: PropTypes.func.isRequired,
    onSuccess: PropTypes.func,
    zIndex: PropTypes.number,
};

export default AdditionalUserFormModal;
